import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { Alert } from 'reactstrap';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import { MAX_PRINT_LABEL_SALE_IDS } from '../../../../constants/settings';
import { resolveMarketplaceLabelData, getLabelBuilderForSale } from '../Helper/MarketplaceLabelHelper';

const PRINT_TRACKING_FALLBACK_ERROR = 'Failed to record the label print. Please try again.';

// print-labels rejects with a specific reason (too many ids, ids not verification_passed,
// unknown ids). PRINT_SALE_LABELS_ERROR is handled by no reducer, so this is the only place
// the operator can see it; without it a 422 reads as "try again" and retrying always fails.
const printTrackingError = (err) => {
  const message = err && err.response && err.response.data && err.response.data.message;
  return message || PRINT_TRACKING_FALLBACK_ERROR;
};

const getSelectableData = (data, selectedData) => {
  const pageIds = new Set(data.map(el => el.id));
  const selectedIds = new Set(selectedData.map(el => el.id));
  const selectedNotOnPage = selectedData.filter(el => !pageIds.has(el.id));
  const remainingSlots = Math.max(0, MAX_PRINT_LABEL_SALE_IDS - selectedNotOnPage.length);
  const selectedOnPage = data.filter(el => selectedIds.has(el.id));
  const unselectedOnPage = data.filter(el => !selectedIds.has(el.id));

  return {
    pageIds,
    selectableData: [
      ...selectedOnPage.slice(0, remainingSlots),
      ...unselectedOnPage.slice(0, Math.max(0, remainingSlots - selectedOnPage.length)),
    ],
  };
};

const PrintSelectionBar = ({
  data,
  selectedData,
  setSelectedData,
  isOpenPrint,
  doPrint,
  onPrinted,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState(null);
  const selectable = useMemo(() => getSelectableData(data, selectedData), [data, selectedData]);

  const isCheckedAll = () => {
    if (!data.length) return false;
    const selectedIds = new Set(selectedData.map(el => el.id));
    return selectable.selectableData.length > 0 && selectable.selectableData.every(el => selectedIds.has(el.id));
  };

  const onSelectAll = () => {
    const { pageIds, selectableData } = selectable;
    const checkedAll = isCheckedAll();
    if (checkedAll) {
      setSelectedData(selectedData.filter(el => !pageIds.has(el.id)));
      return;
    }
    if (!selectableData.length) return;
    const selectedNotOnPage = selectedData.filter(el => !pageIds.has(el.id));
    setSelectedData([
      ...selectedNotOnPage,
      ...selectableData,
    ]);
  };

  const handlePrint = async () => {
    if (!selectedData.length || isPrinting) return;
    setError(null);

    const selectedIds = new Set(selectedData.map(sale => sale.id));
    const selected = data.filter(sale => selectedIds.has(sale.id));
    const saleIds = selected.map(sale => sale.id);
    if (selected.length !== selectedData.length) {
      setError('Some selected sales are no longer available. Refresh the list and select them again.');
      return;
    }
    if (saleIds.length > MAX_PRINT_LABEL_SALE_IDS) {
      setError(`You can print up to ${MAX_PRINT_LABEL_SALE_IDS} labels at a time. Please reduce the selection.`);
      return;
    }

    let printScripts;
    try {
      printScripts = selected.reduce((scripts, sale) => {
        try {
          const labelData = resolveMarketplaceLabelData(sale);
          const builder = getLabelBuilderForSale(sale);
          return [...scripts, ...builder(labelData)];
        } catch (err) {
          console.error(err);
          const order = sale.invoice_number || sale.id;
          throw new Error(`${err.message} (Order ${order})`);
        }
      }, []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to prepare labels for printing.');
      return;
    }

    setIsPrinting(true);
    try {
      // The printer handshake has no side effects, so it runs first: `doPrint` writes
      // printed_by / printed_at for the whole batch and cannot be undone, and a batch
      // flagged printed with nothing printed is worse than one that never started.
      const browserPrintInstance = new ZebraBrowserPrintWrapper();
      const defaultPrinter = await browserPrintInstance.getDefaultPrinter();
      browserPrintInstance.setPrinter(defaultPrinter);
      const printerStatus = await browserPrintInstance.checkPrinterStatus();
      if (!printerStatus.isReadyToPrint) {
        setError(`Printer is not ready: ${printerStatus.errors}`);
        return;
      }

      // Last gate before the ZPL loop: track the print, and send nothing if it fails.
      let trackingResult;
      try {
        trackingResult = await doPrint(saleIds);
      } catch (err) {
        console.error(err);
        setError(printTrackingError(err));
        return;
      }
      if (!trackingResult || !trackingResult.ok) {
        setError(printTrackingError(trackingResult && trackingResult.error));
        return;
      }

      // Sale order preserved, label 1 before label 2, awaited, never interleaved.
      for (const zpl of printScripts) {
        await browserPrintInstance.print(zpl);
      }

      setSelectedData([]);
      if (onPrinted) onPrinted();
    } catch (err) {
      console.error(err);
      setError('Failed to print labels. Please check your printer connection.');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="print-order-pagination">
      <div className="print-wrapper">
        <label className={!isOpenPrint ? 'd-none' : ''} style={{ margin: 0 }}>
          <input
            className="print-order-checkbox"
            type="checkbox"
            name="_allSelected"
            checked={isCheckedAll()}
            onChange={onSelectAll}
          />
          {`${selectedData.length}/${data.length} Selected (max ${MAX_PRINT_LABEL_SALE_IDS})`}
        </label>
        <button
          onClick={handlePrint}
          disabled={isPrinting || !selectedData.length}
          className={
            isOpenPrint && selectedData.length ? 'create-invoice-button' : 'd-none'
          }
          {...(isPrinting && {
            style: {
              backgroundColor: '#e0e0e0',
              cursor: 'not-allowed',
            },
          })}
        >
          {isPrinting ? 'Printing...' : 'Print'}
        </button>
      </div>
      {error ? (
        <Alert color="danger" className="mt-2 mb-0">
          {error}
        </Alert>
      ) : null}
    </div>
  );
};

PrintSelectionBar.propTypes = {
  data: propTypes.array,
  selectedData: propTypes.array,
  setSelectedData: propTypes.func.isRequired,
  isOpenPrint: propTypes.bool,
  doPrint: propTypes.func.isRequired,
  onPrinted: propTypes.func,
};

PrintSelectionBar.defaultProps = {
  data: [],
  selectedData: [],
  isOpenPrint: false,
};

export default PrintSelectionBar;
